/**
 * Application Entry Point
 *
 * Bootstraps the Vue 3 application with:
 * - Vuetify (Material Design)
 * - Vue Router
 * - Pinia (State Management)
 * - Custom reactive store (legacy - will be removed after migration)
 * - Theme management
 * - Authentication state hydration
 */

import { createApp, type Plugin } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import vuetify from './plugins/vuetify'
import router from './router'
import { useUserStore } from './stores/useUserStore'
import { useCartStore } from './stores/useCartStore'
import { useFavoritesStore } from './stores/useFavoritesStore'
import { useRecentlyViewedStore } from './stores/useRecentlyViewedStore'
import { initThemeEarly, installThemeDebugHelper } from './services/themeService'
import { onBeforeUnload } from './utils/lifecycle'
import { configureUsageTracking, installUsageActionTracking } from './utils/telemetry'
import { initSentry, setSentryUserId } from './sentry'
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import './assets/main.css'
import './assets/tokens.css'
import 'leaflet/dist/leaflet.css'

/**
 * Initialize and hydrate user authentication state
 *
 * This runs before mounting to ensure:
 * - Auth tokens are validated
 * - User profile is loaded
 * - Cart, wishlist, and recently viewed are hydrated
 */
async function hydrateAuthState(): Promise<void> {
  try {
    const userStore = useUserStore()
    const cartStore = useCartStore()
    const favoritesStore = useFavoritesStore()
    const recentlyViewedStore = useRecentlyViewedStore()

    // Initialize auth from stored tokens
    await userStore.initFromStorage()

    // Always hydrate carts and recently viewed (both support anonymous sessions)
    const cartsPromise = cartStore.fetchAllCarts()
    const recentlyViewedPromise = recentlyViewedStore.fetchRecentlyViewed()

    // If user is authenticated, load their data
    if (userStore.isLoggedIn) {
      // Load in parallel for better performance
      await Promise.allSettled([
        cartsPromise,
        recentlyViewedPromise,
        favoritesStore.listFavorites(),
      ])
    } else {
      // Ensure anonymous carts and recently viewed are loaded
      await Promise.allSettled([cartsPromise, recentlyViewedPromise])
    }
  } catch (error) {
    console.warn('Failed to hydrate auth state:', error)
  }
}

/**
 * Register application lifecycle handlers
 */
function setupLifecycleHandlers(): void {
  // Clear pending auth actions on page unload
  onBeforeUnload(() => {
    try {
      const userStore = useUserStore()
      userStore.clearPendingActions()
    } catch (error) {
      console.warn('Failed to clear pending actions:', error)
    }
  })
}

/**
 * Bootstrap the application
 */
async function bootstrap(): Promise<void> {
  // Initialize theme as early as possible (before Vue mounts)
  try {
    initThemeEarly()
  } catch (error) {
    console.error('Failed to initialize theme:', error)
  }

  // Create and configure Vue app
  const app = createApp(App)

  // Install Pinia (must be installed before using stores)
  const pinia = createPinia()
  pinia.use(piniaPluginPersistedstate)
  app.use(pinia)

  // Install other plugins — Sentry before router so browserTracingIntegration
  // sees the initial navigation.
  app.use(vuetify as unknown as Plugin)
  initSentry(app, router)
  app.use(router)

  // Hydrate authentication state before mounting
  await hydrateAuthState()

  const userStore = useUserStore()
  if (userStore.isLoggedIn && userStore.profile?.id) {
    setSentryUserId(userStore.profile.id)
  }

  // Configure usage analytics: identity comes from the shopper user store
  // (read lazily so anonymous sessions never expose a user_id).
  configureUsageTracking({
    getUserId: () => useUserStore().profile?.id
  })

  // Install global delegated action tracking; the page feature is read
  // lazily from the current route, so the first navigation event also
  // resolves to the right page.
  installUsageActionTracking(
    () => 'page:' + String(router.currentRoute.value.name || router.currentRoute.value.path)
  )

  await router.isReady()
  app.mount('#app')

  // Setup lifecycle handlers
  setupLifecycleHandlers()

  // Install debug helpers (development only)
  if (import.meta.env.DEV) {
    installThemeDebugHelper()
  }
}

// Start the application
bootstrap().catch((error) => {
  console.error('Failed to bootstrap application:', error)
})
